import { Dispatch, SetStateAction } from "react";
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { ALFRProps, getALFilterRequest } from "./filter-obj";
import { GetAccountListProps, getAccounts } from "@/services/accounts.service";
import { AxiosError } from "axios";
import { handlePermissionError } from "@/utils/permission-check";
import { toaster } from "@/components/ui/toaster";
import { NavigateFunction } from "react-router-dom";

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => Promise<void>;
  getValues: UseFormGetValues<FieldValues>;
}

export const clearFiltersHandler = ({
  setFiltersApplied,
  setValue,
  getValues,
  submitFilters,
}: ClearFiltersHandlerProps) => {
  setValue("social_reason", "");
  setValue("balance_control", []);
  setValue("account_type", "");
  setValue("locality", "");
  setValue("commercial_zone", "");
  setValue("account_number", "");

  submitFilters?.(getValues());
  setFiltersApplied(false);
};

interface SubmitFiltersHandlerProps {
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setCurrentFilters: Dispatch<SetStateAction<{}>>;
  fetchAccounts: (filters?: any) => Promise<void>;
  event: FieldValues;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const submitFiltersHandler = async ({
  event,
  setCurrentFilters,
  setFiltersApplied,
  fetchAccounts,
  setOpenFilters,
  navigate,
  fetchPermissions,
}: SubmitFiltersHandlerProps) => {
  try {
    setFiltersApplied(true);

    const filterObj: Partial<GetAccountListProps> = getALFilterRequest(
      event as ALFRProps,
    );

    setCurrentFilters(filterObj);

    if (!Object.keys(filterObj)?.length) setFiltersApplied(false);
    await fetchAccounts(filterObj);

    setOpenFilters(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudieron aplicar los filtros.",
      });
      throw error;
    }
  }
};

interface FetchAccountsHandlerProps {
  setLoadingAccounts: Dispatch<SetStateAction<boolean>>;
  setAccounts: Dispatch<SetStateAction<any>>;
  filters: any;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  showLoad: boolean;
}

export const fetchAccountsHandler = async ({
  setLoadingAccounts,
  setAccounts,
  filters,
  navigate,
  fetchPermissions,
  showLoad,
}: FetchAccountsHandlerProps) => {
  try {
    if (showLoad) setLoadingAccounts(true);
    const response = await getAccounts({
      ...filters,
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": 10,
    });

    setAccounts(response);

    setLoadingAccounts(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: errorObj?.message ?? "No se pudo cargar el listado de cuentas.",
      });
    }
    throw error;
  }
};
